import { useHelp } from './HelpContext';

interface HelpButtonProps {
  topic: string;
  anchor?: string;
  label?: string;
  variant?: 'solid' | 'ghost' | 'pill' | 'icon';
  className?: string;
  title?: string;
}

/**
 * Shared "?" help trigger. Drop anywhere — clicking opens the drawer on the given topic/anchor.
 *
 * Examples:
 *   <HelpButton topic="wms.estimates.create" />                       // header variant (icon)
 *   <HelpButton topic="wms.tenders.award" anchor="direct-award" />    // deep link to a section
 *   <HelpButton topic="..." variant="pill" label="Help" />            // inline button with text
 */
export default function HelpButton({ topic, anchor, label, variant = 'icon', className = '', title }: HelpButtonProps) {
  const { openHelp } = useHelp();
  const onClick = (e: React.MouseEvent) => { e.stopPropagation(); openHelp(topic, anchor); };
  const computedTitle = title ?? (label ? `${label} — Help` : 'Help');

  if (variant === 'pill') {
    return (
      <button type="button" onClick={onClick} title={computedTitle}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 hover:bg-white/30 text-white text-xs transition-colors ${className}`}
      >
        <i className="ri-question-line"></i>
        {label ?? 'Help'}
      </button>
    );
  }

  if (variant === 'ghost') {
    return (
      <button type="button" onClick={onClick} title={computedTitle}
        className={`inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs ${className}`}
      >
        <i className="ri-question-line"></i>
        {label ?? 'Help'}
      </button>
    );
  }

  if (variant === 'solid') {
    return (
      <button type="button" onClick={onClick} title={computedTitle}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs ${className}`}
      >
        <i className="ri-question-line"></i>
        {label ?? 'Help'}
      </button>
    );
  }

  // icon (default)
  return (
    <button type="button" onClick={onClick} title={computedTitle}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors ${className}`}
      aria-label={computedTitle}
    >
      <i className="ri-question-line"></i>
    </button>
  );
}
